import type { ScriptVariable } from "Infrastructure/Api/Api";
import type {
  JobScriptFE,
  ScriptSegmentFE,
} from "Projects/Components/ScriptEditor/ScriptEditorContext";
import { isVoiceSegment } from "Projects/Components/ScriptEditor/ScriptEditorContext";
import { extractVariableNames } from "./parseSegmentText";

export function collectAllVariableNames(
  segments: ScriptSegmentFE[],
): Set<string> {
  const names = new Set<string>();

  for (const seg of segments) {
    if (isVoiceSegment(seg)) {
      for (const span of seg.spans) {
        for (const name of extractVariableNames(span.text)) {
          names.add(name);
        }
      }
    }
  }

  return names;
}

export type SyncVariablesResult = {
  /** Updated variables list */
  variables: ScriptVariable[];
  /**
   * Variables that were removed because they are no longer referenced,
   * but had a non-empty defaultValue. UI should warn the user about these.
   */
  removedWithValue: ScriptVariable[];
};

/**
 * Synchronises the Variables list after text changes:
 *  - New {{varName}} references → added with empty defaultValue
 *  - Variables no longer referenced with empty defaultValue → silently removed
 *  - Variables no longer referenced with non-empty defaultValue → returned in
 *    `removedWithValue` so UI can ask for confirmation before removing
 */
export function syncVariables(
  currentVars: ScriptVariable[],
  usedNames: Set<string>,
): SyncVariablesResult {
  const existing = new Map(currentVars.map((v) => [v.name, v]));

  // Build new list: keep only variables that are still referenced
  const kept: ScriptVariable[] = [];
  for (const name of usedNames) {
    kept.push(existing.get(name) ?? { name, defaultValue: "" });
  }

  // Find variables that are no longer used but have a value set
  const removedWithValue: ScriptVariable[] = [];
  for (const [name, v] of existing) {
    if (!usedNames.has(name) && v.defaultValue) {
      removedWithValue.push(v);
    }
  }

  return { variables: kept, removedWithValue };
}

/**
 * Convenience helper: given the full script, returns an updated variables list.
 * Variables with non-empty defaultValue that are no longer used are kept
 * (conservative mode — they're only removed after explicit user confirmation).
 */
export function syncVariablesConservative(
  script: JobScriptFE,
): ScriptVariable[] {
  const usedNames = collectAllVariableNames(script.segments);
  const existing = new Map(script.variables.map((v) => [v.name, v]));

  const result: ScriptVariable[] = [];

  // Add all currently-used variables (new ones get empty defaultValue)
  for (const name of usedNames) {
    result.push(existing.get(name) ?? { name, defaultValue: "" });
  }

  // Keep variables with a defaultValue even if no longer used in text,
  // so the user doesn't lose data accidentally
  for (const [name, v] of existing) {
    if (!usedNames.has(name) && v.defaultValue) {
      result.push(v);
    }
  }

  return result;
}
